import { Vehicle } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertCircle, XCircle, Calendar, Wrench, Users } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ConditionHistoryProps {
  vehicle: Vehicle;
}

export const ConditionHistory = ({ vehicle }: ConditionHistoryProps) => {
  const getConditionBadge = (condition?: Vehicle['generalCondition']) => {
    if (!condition) return null;
    
    const variants = {
      'excellent': { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle, label: 'Excellent' },
      'tres-bon': { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: CheckCircle, label: 'Très bon' },
      'bon': { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: CheckCircle, label: 'Bon' },
      'moyen': { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: AlertCircle, label: 'Moyen' },
      'a-renover': { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, label: 'À rénover' },
    };
    
    const variant = variants[condition];
    const Icon = variant.icon;
    
    return (
      <Badge className={`${variant.color} border flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {variant.label}
      </Badge>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Non renseigné';
    try {
      return format(new Date(dateString), 'PPP', { locale: fr });
    } catch {
      return dateString;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">État & Historique</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* État général */}
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <div className="text-sm font-medium text-muted-foreground mb-2">État général</div>
            {getConditionBadge(vehicle.generalCondition)}
          </div>
          <div>
            <div className="text-sm font-medium text-muted-foreground mb-2">État intérieur</div>
            {getConditionBadge(vehicle.interiorCondition)}
          </div>
          <div>
            <div className="text-sm font-medium text-muted-foreground mb-2">État extérieur</div>
            {getConditionBadge(vehicle.exteriorCondition)}
          </div>
        </div>

        {/* Entretien */}
        {(vehicle.lastServiceDate || vehicle.nextServiceDue) && (
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <Wrench className="h-5 w-5 text-primary" />
              Entretien
            </div>
            
            {vehicle.lastServiceDate && (
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="font-medium">Dernier entretien</div>
                  <div className="text-sm text-muted-foreground">
                    {formatDate(vehicle.lastServiceDate)}
                  </div>
                </div>
              </div>
            )}
            
            {vehicle.nextServiceDue && (
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="font-medium">Prochain entretien prévu</div>
                  <div className="text-sm text-muted-foreground">
                    À {vehicle.nextServiceDue.toLocaleString('fr-FR')} km
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Contrôle technique */}
        {(vehicle.technicalInspectionDate || vehicle.technicalInspectionValidUntil) && (
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <CheckCircle className="h-5 w-5 text-primary" />
              Contrôle technique
            </div>
            
            {vehicle.technicalInspectionDate && (
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="font-medium">Date du contrôle</div>
                  <div className="text-sm text-muted-foreground">
                    {formatDate(vehicle.technicalInspectionDate)}
                  </div>
                </div>
              </div>
            )}
            
            {vehicle.technicalInspectionValidUntil && (
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="font-medium">Valide jusqu'au</div>
                  <div className="text-sm text-muted-foreground">
                    {formatDate(vehicle.technicalInspectionValidUntil)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Historique */}
        {(vehicle.previousOwners || vehicle.accidentHistory !== undefined) && (
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <Users className="h-5 w-5 text-primary" />
              Historique
            </div>
            
            {vehicle.previousOwners !== undefined && (
              <div className="flex items-center justify-between">
                <span className="font-medium">Propriétaires précédents</span>
                <Badge variant="outline">{vehicle.previousOwners}</Badge>
              </div>
            )}
            
            {vehicle.accidentHistory !== undefined && (
              <div className="flex items-center justify-between">
                <span className="font-medium">Historique d'accidents</span>
                <Badge variant={vehicle.accidentHistory ? 'destructive' : 'outline'}>
                  {vehicle.accidentHistory ? 'Oui' : 'Aucun'}
                </Badge>
              </div>
            )}
            
            {vehicle.accidentHistory && vehicle.accidentDetails && (
              <div className="mt-2 p-3 bg-muted rounded-md">
                <div className="text-sm font-medium mb-1">Détails</div>
                <div className="text-sm text-muted-foreground">{vehicle.accidentDetails}</div>
              </div>
            )}
          </div>
        )}

        {/* Pneus */}
        {(vehicle.frontTireSize || vehicle.rearTireSize || vehicle.tireWearPercentage) && (
          <div className="space-y-3 pt-4 border-t">
            <div className="text-lg font-semibold">Pneumatiques</div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {vehicle.frontTireSize && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Pneus avant</div>
                  <div>{vehicle.frontTireSize}</div>
                </div>
              )}
              
              {vehicle.rearTireSize && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Pneus arrière</div>
                  <div>{vehicle.rearTireSize}</div>
                </div>
              )}
            </div>
            
            {vehicle.tireWearPercentage && (
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">Usure des pneus</div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-primary h-full transition-all"
                      style={{ width: `${vehicle.tireWearPercentage}%` }}
                    />
                  </div>
                  <span className="font-semibold">{vehicle.tireWearPercentage}%</span>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
